import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { retryUnlessRefused } from '../../lib/load-failure';
import { blurOnWheel } from '../../lib/number-input';
import {
  CADENCE_TRIGGERS,
  INTERVAL_TRIGGERS,
  defaultCadenceFor,
  describePolicy,
  formTypeForEquipmentType,
  policyBody,
  policyDraftError,
  type PolicyDraft,
  type PolicyKind,
  type ServicePolicy,
} from '../../lib/service-policies';

/**
 * "Service & inspection" for one equipment type (FE-5; API-9).
 *
 * The values are the TENANT's. Nothing is prefilled; with no policy rows the
 * panel says it is using FieldIQ's defaults and names them — and only after a
 * SUCCESSFUL load, because "Using defaults" over a failed request would tell
 * the office the type is configured when nobody knows.
 *
 * Per-asset cadence overrides are rows with an equipment_id; they are counted
 * here and managed on the asset (EquipmentDetail), where the asset is known.
 */
const inputCls = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

const emptyDraft = (kind: PolicyKind): PolicyDraft => ({
  kind,
  trigger_type: kind === 'inspection_cadence' ? 'calendar_days' : 'calendar_days',
  interval: '',
  name: '',
});

export default function ServicePoliciesPanel({
  typeId,
  typeName,
  canWrite,
}: {
  typeId: string;
  typeName: string;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const formType = formTypeForEquipmentType(typeName);
  const defaultCadence = defaultCadenceFor(typeName);

  const query = useQuery<ServicePolicy[]>({
    queryKey: ['service-policies', typeId],
    queryFn: () => api.equipmentTypes.servicePolicies.list(typeId).then((r) => unwrapArray<ServicePolicy>(r.data)),
    retry: retryUnlessRefused(),
  });

  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['service-policies', typeId] });
    queryClient.invalidateQueries({ queryKey: ['service-policies-resolved'] });
  };
  const save = useMutation({
    mutationFn: (d: PolicyDraft) =>
      editId
        ? api.equipmentTypes.servicePolicies.update(typeId, editId, policyBody(d))
        : api.equipmentTypes.servicePolicies.create(typeId, policyBody(d, { form_type: formType })),
    onSuccess: () => { setDraft(null); setEditId(null); setFormError(null); refresh(); },
    onError: (err: any) => setFormError(err?.response?.data?.error || 'Failed to save the policy'),
  });
  const remove = useMutation({
    mutationFn: (p: ServicePolicy) => api.equipmentTypes.servicePolicies.remove(typeId, p.id),
    onSuccess: refresh,
    onError: (err: any) => setFormError(err?.response?.data?.error || 'Failed to remove the policy'),
  });

  const submit = () => {
    if (!draft) return;
    const problem = policyDraftError(draft);
    if (problem) { setFormError(problem); return; }
    save.mutate(draft);
  };

  if (query.isLoading) {
    return <div className="py-3"><Loader2 className="w-4 h-4 text-fieldiq-accent animate-spin" aria-label="Loading policies" /></div>;
  }
  if (query.error) {
    return <LoadFailureNotice error={query.error} subject="service policies" allowed="admins, office staff, supervisors and dispatchers" className="py-4" />;
  }

  const rows = query.data ?? [];
  const typeRows = rows.filter((p) => p.equipmentId == null);
  const cadence = typeRows.filter((p) => p.kind === 'inspection_cadence');
  const intervals = typeRows.filter((p) => p.kind === 'service_interval');
  const overrides = rows.filter((p) => p.equipmentId != null && p.kind === 'inspection_cadence');

  const startEdit = (p: ServicePolicy) => {
    setEditId(p.id);
    setFormError(null);
    setDraft({ kind: p.kind, trigger_type: p.triggerType, interval: p.intervalValue == null ? '' : String(p.intervalValue), name: p.name });
  };

  const row = (p: ServicePolicy) => (
    <li key={p.id} className="flex items-center gap-2 text-sm">
      <span className="text-white">{p.name}</span>
      <span className="text-fieldiq-muted">— {describePolicy(p)}</span>
      {!p.isActive && <span className="text-[10px] uppercase text-fieldiq-muted">inactive</span>}
      {canWrite && (
        <>
          <button className="ml-auto text-xs text-fieldiq-accent hover:text-white" aria-label={`Edit ${p.name}`} onClick={() => startEdit(p)}>Edit</button>
          <button
            className="text-xs text-red-400 hover:text-red-300"
            aria-label={`Remove ${p.name}`}
            onClick={() => { if (window.confirm(`Remove “${p.name}”? The type falls back to the default.`)) remove.mutate(p); }}
          >
            Remove
          </button>
        </>
      )}
    </li>
  );

  const triggers = draft?.kind === 'inspection_cadence'
    ? CADENCE_TRIGGERS.map((t) => ({ value: t.value, label: t.label }))
    : INTERVAL_TRIGGERS.map((t) => ({ value: t.value, label: t.label }));

  return (
    <div className="space-y-3 pt-3 border-t border-fieldiq-border" data-testid={`service-policies-${typeId}`}>
      <div>
        <p className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wider mb-1">Inspection cadence</p>
        {cadence.length > 0 ? (
          <ul className="space-y-1">{cadence.map(row)}</ul>
        ) : defaultCadence ? (
          <p className="text-sm text-fieldiq-text">Using defaults: <span className="text-white">{defaultCadence}</span> (FieldIQ default)</p>
        ) : (
          <p className="text-sm text-fieldiq-muted">This type has no inspection form, so no cadence applies.</p>
        )}
        {overrides.length > 0 && (
          <p className="text-xs text-fieldiq-muted mt-1">
            {overrides.length} asset{overrides.length === 1 ? ' has its' : 's have their'} own cadence — see the asset.
          </p>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wider mb-1">Service intervals</p>
        {intervals.length > 0
          ? <ul className="space-y-1">{intervals.map(row)}</ul>
          : <p className="text-sm text-fieldiq-muted">None set for this type. An asset's own maintenance schedule still applies.</p>}
      </div>

      {canWrite && !draft && (
        <div className="flex gap-3">
          {formType && (
            <button className="text-xs font-semibold text-fieldiq-accent hover:underline" onClick={() => { setDraft(emptyDraft('inspection_cadence')); setEditId(null); setFormError(null); }}>
              + Inspection cadence
            </button>
          )}
          <button className="text-xs font-semibold text-fieldiq-accent hover:underline" onClick={() => { setDraft(emptyDraft('service_interval')); setEditId(null); setFormError(null); }}>
            + Service interval
          </button>
        </div>
      )}

      {draft && (
        <div role="group" aria-label={editId ? 'Edit policy' : 'New policy'} className="flex flex-wrap items-center gap-2">
          <input
            aria-label="Policy name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={draft.kind === 'inspection_cadence' ? 'e.g. Monthly inspection' : 'e.g. Hydraulic service'}
            className={`${inputCls} w-48`}
          />
          <select
            aria-label="Trigger"
            value={draft.trigger_type}
            onChange={(e) => setDraft({ ...draft, trigger_type: e.target.value })}
            className={inputCls}
          >
            {triggers.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {draft.trigger_type !== 'per_job' && (
            <input
              aria-label="N"
              type="number"
              min={1}
              step={draft.kind === 'inspection_cadence' ? 1 : 'any'}
              onWheel={blurOnWheel}
              value={draft.interval}
              onChange={(e) => setDraft({ ...draft, interval: e.target.value })}
              placeholder="N"
              className={`${inputCls} w-20`}
            />
          )}
          <button
            onClick={submit}
            disabled={save.isPending}
            className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
          >
            Save
          </button>
          <button onClick={() => { setDraft(null); setEditId(null); setFormError(null); }} className="px-2 py-1.5 text-xs text-fieldiq-text">Cancel</button>
        </div>
      )}
      {formError && <p className="text-xs text-red-400">{formError}</p>}
    </div>
  );
}
